/**
 * StripeConnectPanel — drop-in card for the Settings page that gives
 * users a one-click "Connect with Stripe" button (OAuth) instead of
 * pasting a restricted key. Falls back to a friendly explanation when
 * Connect is not yet enabled (admin hasn't set STRIPE_CONNECT_CLIENT_ID).
 *
 * Behaviour:
 *   1. On mount → GET /api/stripe-connect/status
 *   2. If `enabled: false` — show "Admin must enable Connect first" banner
 *      with a link to the developer docs section explaining setup.
 *   3. If `enabled: true && connected: false` — show big emerald "Connect
 *      with Stripe" button. Click → POST /start → redirect to authorize_url.
 *   4. If `connected: true` — show emerald check + masked stripe_user_id
 *      and a Disconnect button.
 *   5. URL query `?stripe_connect=ok` triggers a success toast on return.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  ShieldCheck, AlertTriangle, ExternalLink, Loader2, CheckCircle2,
  Plug, Unplug,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StripeConnectPanel = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  const auth = () => {
    const t = localStorage.getItem('token');
    return t ? { Authorization: `Bearer ${t}` } : {};
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/stripe-connect/status`, { headers: auth() });
      setStatus(data);
    } catch {
      setStatus({ enabled: false, connected: false });
    } finally {
      setLoading(false);
    }
  };

  // One-off success/failure toast from OAuth callback.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sc = params.get('stripe_connect');
    if (sc === 'ok') {
      toast.success('Stripe connected — your customer P&L will sync within a minute.');
      // Clean the URL so the toast doesn't fire again on next mount.
      window.history.replaceState({}, '', window.location.pathname);
    } else if (sc === 'error') {
      const detail = params.get('detail') || 'Unknown error';
      toast.error(`Stripe connection failed: ${decodeURIComponent(detail)}`);
      window.history.replaceState({}, '', window.location.pathname);
    }
    load();
  }, []);

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const { data } = await axios.get(
        `${API_URL}/stripe-connect/start`,
        { headers: auth(), params: { return_to: '/settings' } },
      );
      // Hand off to Stripe — full top-level navigation, not popup.
      window.location.href = data.authorize_url;
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Could not start Connect flow.';
      toast.error(detail);
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm("Disconnect Stripe? Your existing P&L will keep showing, but new revenue won't sync until you reconnect.")) return;
    setDisconnecting(true);
    try {
      await axios.post(`${API_URL}/stripe-connect/disconnect`, {}, { headers: auth() });
      toast.success('Stripe disconnected.');
      await load();
    } catch {
      toast.error('Disconnect failed — please try again.');
    } finally {
      setDisconnecting(false);
    }
  };

  if (loading) {
    return (
      <Card className="bg-slate-900 border-white/10">
        <CardContent className="p-6 flex items-center gap-2 text-slate-400">
          <Loader2 className="w-4 h-4 animate-spin" /> Checking Stripe Connect status…
        </CardContent>
      </Card>
    );
  }

  // Connect not enabled by platform admin yet → friendly fallback.
  if (!status?.enabled) {
    return (
      <Card className="bg-slate-900 border-amber-500/30" data-testid="stripe-connect-disabled">
        <CardHeader>
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-400" />
            <CardTitle className="text-base text-amber-100">Stripe Connect is not enabled yet</CardTitle>
          </div>
          <CardDescription className="text-amber-200/80">
            Your CARTIE admin can enable one-click Stripe Connect by registering a Connect application
            on dashboard.stripe.com → Connect → Settings, then setting{' '}
            <code className="text-emerald-300">STRIPE_CONNECT_CLIENT_ID</code> in the backend{' '}
            <code className="text-emerald-300">.env</code>.
          </CardDescription>
        </CardHeader>
        <CardContent className="text-sm text-slate-400">
          Until then, you can paste a Stripe restricted key in the Customer P&amp;L sync flow —
          works just as well, just one extra step.
        </CardContent>
      </Card>
    );
  }

  // Already connected
  if (status.connected) {
    return (
      <Card className="bg-emerald-500/5 border-emerald-500/30" data-testid="stripe-connect-connected">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-300" />
              <CardTitle className="text-base text-white">Stripe is connected</CardTitle>
              <Badge variant="outline" className="border-emerald-400/40 text-emerald-300 text-[11px]">
                Read-only
              </Badge>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={handleDisconnect}
              disabled={disconnecting}
              className="border-rose-400/40 text-rose-300 hover:text-rose-200 hover:bg-rose-500/10"
              data-testid="stripe-disconnect-btn"
            >
              {disconnecting
                ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Disconnecting</>
                : <><Unplug className="w-4 h-4 mr-1.5" /> Disconnect</>}
            </Button>
          </div>
          <CardDescription className="text-slate-400 mt-2">
            Connected account: <code className="text-emerald-300">{status.stripe_user_id}</code>
            {status.connected_at && <> · connected {new Date(status.connected_at).toLocaleDateString()}</>}
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  // Enabled, not connected → primary CTA
  return (
    <Card className="bg-gradient-to-br from-emerald-500/10 to-cyan-500/5 border-emerald-500/30" data-testid="stripe-connect-cta">
      <CardHeader>
        <div className="flex items-center gap-2">
          <Plug className="w-5 h-5 text-emerald-300" />
          <CardTitle className="text-base text-white">Connect with Stripe</CardTitle>
          <Badge variant="outline" className="border-emerald-400/40 text-emerald-300 text-[11px]">
            Recommended
          </Badge>
        </div>
        <CardDescription className="text-slate-400">
          One click on Stripe → approve once → CARTIE never sees a raw key. Safer than pasting a restricted key.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Button
          onClick={handleConnect}
          disabled={connecting}
          className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold"
          data-testid="stripe-connect-btn"
        >
          {connecting
            ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Redirecting to Stripe…</>
            : <><Plug className="w-4 h-4 mr-1.5" /> Connect with Stripe <ExternalLink className="w-3.5 h-3.5 ml-1.5 opacity-70" /></>}
        </Button>
        <p className="text-xs text-slate-400 mt-3 flex items-center gap-1.5">
          <ShieldCheck className="w-3 h-3 text-emerald-400" />
          Read-only scope only. We never modify, refund, or charge.
        </p>
      </CardContent>
    </Card>
  );
};

export default StripeConnectPanel;
